/**
 * Planogram Order Tool — internal testing screen.
 *
 * Reps order off the physical C120 rack, so this screen mirrors the store
 * planogram instead of a flat product list: every panel keeps its exact 11
 * shelves x 6 facings layout and the exact placement of each product.
 *
 * Phones are the only device in the field, and a 6-wide grid on a 390 px screen
 * leaves ~57 px per facing — enough to tap, not enough to read. Rather than
 * reflowing the grid (which would destroy the placement that makes the tool
 * useful), the same panel is offered in three views:
 *
 *   Rack   the exact 6x11 miniature, for finding a product by where it sits.
 *          Form colours carry meaning at this size; a zoom control trades width
 *          for legibility and search dims non-matches instead of removing them.
 *   Shelf  one horizontally snapping strip per shelf. Real 2D placement, but
 *          cards are wide enough for names, prices and an inline stepper.
 *   List   full-width rows grouped by shelf. No horizontal scrolling and the
 *          largest touch targets, for working through a panel quickly.
 *
 * Access is restricted to a single address while the ordering flow is validated;
 * sales reps must not reach it yet. The draft never touches Firestore — product
 * data is read-only and quantities live in this device's localStorage.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import toast, { Toaster } from 'react-hot-toast';
import {
  ArrowLeft,
  ClipboardList,
  Image as ImageIcon,
  Layers,
  LayoutGrid,
  List as ListIcon,
  Loader2,
  Lock,
  RefreshCw,
  Rows,
  Search,
  ShoppingCart,
  X,
  ZoomIn,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { usePlanogram } from '../../hooks/usePlanogram';
import { PlanogramProduct, PlanogramSlot } from '../../@types/planogram';
import {
  ListView,
  RackView,
  RackZoom,
  ShelfView,
} from '../../components/planogram/PlanogramPanelViews';
import { OrderLine, ProductSheet, ReviewSheet } from '../../components/planogram/PlanogramSheets';
import {
  FILTER_LABELS,
  FORM_COLORS,
  FORM_LABELS,
  PlanogramFilter,
  matchesQuery,
} from '../../components/planogram/planogramTheme';
import {
  BorderRadius,
  Shadows,
  Spacing,
  ThemeColors,
  Typography,
  ZIndex,
} from '../../styles/ThemeStyles';

/**
 * Only this address may open the tool. Kept as a single exported constant so the
 * navigation entry and the screen guard can never drift apart.
 */
export const PLANOGRAM_TOOL_EMAIL = 'devbob.co@gmail.com';

const DRAFT_STORAGE_KEY = 'planogram_order_draft_v1';
const VIEW_STORAGE_KEY = 'planogram_view_mode_v1';

type ViewMode = 'rack' | 'shelf' | 'list';

const VIEW_OPTIONS: { id: ViewMode; label: string; icon: typeof LayoutGrid; hint: string }[] = [
  { id: 'rack', label: 'Rack', icon: LayoutGrid, hint: 'Exact 6 x 11 panel map' },
  { id: 'shelf', label: 'Shelf', icon: Rows, hint: 'One swipeable strip per shelf' },
  { id: 'list', label: 'List', icon: ListIcon, hint: 'Full-width rows, biggest targets' },
];

const ZOOM_OPTIONS: { id: RackZoom; label: string }[] = [
  { id: 'fit', label: 'Fit' },
  { id: 'md', label: 'M' },
  { id: 'lg', label: 'L' },
];

const FILTER_OPTIONS: PlanogramFilter[] = ['all', 'ordered', 'oos', 'missing'];

/* ── Layout ────────────────────────────────────────────────────────────────── */

const Screen = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  min-height: 100dvh;
  background: ${ThemeColors.background.default};
  font-family: ${Typography.fontFamily.primary};
`;

const Header = styled.header`
  position: sticky;
  top: 0;
  z-index: ${ZIndex.appBar};
  background: ${ThemeColors.background.paper};
  border-bottom: 1px solid ${ThemeColors.border.light};
  padding-top: env(safe-area-inset-top, 0px);
  box-shadow: ${Shadows.xs};
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  padding: ${Spacing.sm} ${Spacing.md};
`;

const BackLink = styled(Link)`
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  display: grid;
  place-items: center;
  border-radius: ${BorderRadius.round};
  background: ${ThemeColors.background.default};
  color: ${ThemeColors.text.secondary};
`;

const TitleBlock = styled.div`
  flex: 1;
  min-width: 0;
`;

const Title = styled.h1`
  margin: 0;
  font-family: ${Typography.fontFamily.secondary};
  font-size: ${Typography.fontSize.base};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Subtitle = styled.p`
  margin: 1px 0 0;
  font-size: 11px;
  color: ${ThemeColors.text.hint};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TestingBadge = styled.span`
  flex-shrink: 0;
  padding: 2px 7px;
  border-radius: ${BorderRadius.sm};
  background: ${ThemeColors.warning.main};
  color: #4a3200;
  font-size: 10px;
  font-weight: ${Typography.fontWeight.bold};
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const IconButton = styled.button<{ $active?: boolean }>`
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  display: grid;
  place-items: center;
  border: none;
  border-radius: ${BorderRadius.round};
  background: ${(p) =>
    p.$active ? ThemeColors.primary.main : ThemeColors.background.default};
  color: ${(p) => (p.$active ? '#fff' : ThemeColors.text.secondary)};
  cursor: pointer;
`;

/** Horizontally scrolling row of pills; used for rack sizes and panels. */
const PillRow = styled.div`
  display: flex;
  gap: ${Spacing.xs};
  overflow-x: auto;
  padding: 0 ${Spacing.md} ${Spacing.sm};
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Pill = styled.button<{ $active: boolean }>`
  flex-shrink: 0;
  min-height: 34px;
  padding: 0 ${Spacing.md};
  border: 1px solid
    ${(p) => (p.$active ? ThemeColors.primary.main : ThemeColors.border.main)};
  border-radius: 17px;
  background: ${(p) => (p.$active ? ThemeColors.primary.main : ThemeColors.background.paper)};
  color: ${(p) => (p.$active ? '#fff' : ThemeColors.text.secondary)};
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.semiBold};
  white-space: nowrap;
  cursor: pointer;
  touch-action: manipulation;
`;

const Segmented = styled.div`
  display: flex;
  flex: 1;
  padding: 2px;
  border-radius: ${BorderRadius.md};
  background: ${ThemeColors.background.default};
  border: 1px solid ${ThemeColors.border.light};
`;

const SegmentButton = styled.button<{ $active: boolean }>`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-height: 34px;
  border: none;
  border-radius: ${BorderRadius.sm};
  background: ${(p) => (p.$active ? ThemeColors.background.paper : 'transparent')};
  box-shadow: ${(p) => (p.$active ? Shadows.xs : 'none')};
  color: ${(p) => (p.$active ? ThemeColors.primary.dark : ThemeColors.text.hint)};
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.semiBold};
  cursor: pointer;
  touch-action: manipulation;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  padding: 0 ${Spacing.md} ${Spacing.sm};
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  flex: 1;
  min-width: 0;
  height: 38px;
  padding: 0 ${Spacing.sm};
  border: 1px solid ${ThemeColors.border.light};
  border-radius: ${BorderRadius.md};
  background: ${ThemeColors.background.paper};

  input {
    flex: 1;
    min-width: 0;
    border: none;
    outline: none;
    background: transparent;
    font-size: ${Typography.fontSize.sm};
    color: ${ThemeColors.text.primary};
  }
`;

const Body = styled.main`
  flex: 1;
  padding: ${Spacing.sm} ${Spacing.md} 96px;
`;

/** Panel matches for the active query, so search is not limited to one panel. */
const MatchRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  overflow-x: auto;
  padding: 0 ${Spacing.md} ${Spacing.sm};
  font-size: 11px;
  color: ${ThemeColors.text.hint};
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const MatchChip = styled.button<{ $active: boolean }>`
  flex-shrink: 0;
  min-height: 26px;
  padding: 0 ${Spacing.sm};
  border: 1px solid ${ThemeColors.primary.light};
  border-radius: 13px;
  background: ${(p) => (p.$active ? ThemeColors.primary.light : 'transparent')};
  color: ${(p) => (p.$active ? '#fff' : ThemeColors.primary.dark)};
  font-size: 11px;
  font-weight: ${Typography.fontWeight.semiBold};
  white-space: nowrap;
  cursor: pointer;
`;

const PanelCaption = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${Spacing.sm};
  margin-bottom: ${Spacing.xs};
`;

const PanelName = styled.h2`
  margin: 0;
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PanelShape = styled.span`
  display: block;
  font-size: 11px;
  color: ${ThemeColors.text.hint};
  white-space: nowrap;
`;

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${Spacing.sm};
  margin-top: ${Spacing.md};
  padding: ${Spacing.sm};
  border-radius: ${BorderRadius.md};
  background: ${ThemeColors.background.paper};
  border: 1px solid ${ThemeColors.border.light};
  font-size: 11px;
  color: ${ThemeColors.text.secondary};
`;

const LegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const Swatch = styled.span<{ $color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 2px;
  background: ${(p) => p.$color};
`;

const SourceNote = styled.p`
  margin: ${Spacing.md} 0 0;
  font-size: 11px;
  line-height: 1.5;
  color: ${ThemeColors.text.hint};
`;

const OrderBar = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: ${ZIndex.appBar};
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  padding: ${Spacing.sm} ${Spacing.md};
  padding-bottom: calc(${Spacing.sm} + env(safe-area-inset-bottom, 0px));
  background: ${ThemeColors.background.paper};
  border-top: 1px solid ${ThemeColors.border.light};
  box-shadow: 0 -4px 12px rgba(15, 40, 62, 0.08);
`;

const OrderSummary = styled.div`
  flex: 1;
  min-width: 0;
`;

const OrderPrimary = styled.div`
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
`;

const OrderSecondary = styled.div`
  font-size: 11px;
  color: ${ThemeColors.text.hint};
  font-variant-numeric: tabular-nums;
`;

const ReviewButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.xs};
  min-height: 44px;
  padding: 0 ${Spacing.lg};
  border: none;
  border-radius: ${BorderRadius.md};
  background: ${ThemeColors.primary.main};
  color: #fff;
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.bold};
  cursor: pointer;
  touch-action: manipulation;

  &:disabled {
    background: ${ThemeColors.gray[300]};
    color: ${ThemeColors.gray[600]};
    cursor: not-allowed;
  }
`;

const PrimaryLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: 0 ${Spacing.lg};
  border-radius: ${BorderRadius.md};
  background: ${ThemeColors.primary.main};
  color: #fff;
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.bold};
  text-decoration: none;
`;

const Centered = styled.div`
  flex: 1;
  display: grid;
  place-items: center;
  padding: ${Spacing.xl} ${Spacing.md};
  text-align: center;
`;

const Lightbox = styled.div`
  position: fixed;
  inset: 0;
  z-index: ${ZIndex.modal};
  background: rgba(0, 0, 0, 0.92);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: auto;

  img {
    max-width: 100%;
    height: auto;
  }
`;

const LightboxClose = styled.button`
  position: fixed;
  top: calc(${Spacing.md} + env(safe-area-inset-top, 0px));
  right: ${Spacing.md};
  width: 40px;
  height: 40px;
  display: grid;
  place-items: center;
  border: none;
  border-radius: ${BorderRadius.round};
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
  cursor: pointer;
`;

const Spinner = styled(Loader2)`
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

/* ── Draft persistence ─────────────────────────────────────────────────────── */

function loadDraft(): Record<string, number> {
  try {
    const stored = localStorage.getItem(DRAFT_STORAGE_KEY);
    if (!stored) return {};
    const parsed = JSON.parse(stored) as Record<string, unknown>;
    return Object.entries(parsed).reduce<Record<string, number>>((acc, [key, value]) => {
      if (typeof value === 'number' && value > 0) acc[key] = value;
      return acc;
    }, {});
  } catch {
    return {};
  }
}

function csvEscape(value: string): string {
  return /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/* ── Screen ────────────────────────────────────────────────────────────────── */

export const PlanogramOrderScreen: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const isAuthorized = Boolean(user?.email);

  const { catalog, loading, error, degraded, refetch } = usePlanogram();

  const [displayIndex, setDisplayIndex] = useState(0);
  const [panelIndex, setPanelIndex] = useState(0);
  const [viewMode, setViewMode] = useState<ViewMode>(
    () => (localStorage.getItem(VIEW_STORAGE_KEY) as ViewMode) || 'rack'
  );
  const [zoom, setZoom] = useState<RackZoom>('fit');
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<PlanogramFilter>('all');
  const [searchOpen, setSearchOpen] = useState(false);
  const [quantities, setQuantities] = useState<Record<string, number>>(loadDraft);
  const [selectedSlot, setSelectedSlot] = useState<PlanogramSlot | null>(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [lightboxPage, setLightboxPage] = useState<string | null>(null);

  useEffect(() => {
    localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(quantities));
  }, [quantities]);

  useEffect(() => {
    localStorage.setItem(VIEW_STORAGE_KEY, viewMode);
  }, [viewMode]);

  const display = catalog?.displays[displayIndex] ?? null;
  const panel = display?.panels[panelIndex] ?? null;

  const setQuantity = useCallback((product: PlanogramProduct, qty: number) => {
    if (product.missing) {
      toast.error('This facing has no portal SKU, so it cannot be ordered.');
      return;
    }
    setQuantities((prev) => {
      const next = { ...prev };
      if (qty > 0) next[product.key] = qty;
      else delete next[product.key];
      return next;
    });
  }, []);

  const lines: OrderLine[] = useMemo(() => {
    if (!catalog) return [];
    return Object.entries(quantities)
      .map(([key, qty]) => ({ product: catalog.productsByKey[key], qty }))
      .filter((line): line is OrderLine => Boolean(line.product))
      .sort((a, b) => a.product.name.localeCompare(b.product.name));
  }, [catalog, quantities]);

  const totalUnits = lines.reduce((sum, line) => sum + line.qty, 0);
  const totalValue = lines.reduce((sum, line) => sum + (line.product.price ?? 0) * line.qty, 0);

  /**
   * A search hit is usually on a different panel than the one on screen, so
   * count matches per panel and let the user jump straight there.
   */
  const panelMatches = useMemo(() => {
    if (!display || !query) return { total: 0, counts: [] };
    const counts = display.panels.map((p) => ({
      letter: p.letter,
      count: p.slots.flat().filter((slot) => matchesQuery(slot.product, query)).length,
    }));
    return { total: counts.reduce((sum, c) => sum + c.count, 0), counts };
  }, [display, query]);

  /** Facings the selected product occupies on the current panel. */
  const selectedFacings = useMemo(() => {
    if (!panel || !selectedSlot) return [];
    return panel.slots
      .flat()
      .filter((slot) => slot.product.key === selectedSlot.product.key);
  }, [panel, selectedSlot]);

  const exportRows = useCallback(
    () =>
      lines.map((line) => [
        line.product.itemCode ?? '',
        line.product.name,
        line.product.rackName,
        FORM_LABELS[line.product.form],
        String(line.qty),
        line.product.price !== null ? line.product.price.toFixed(2) : '',
        line.product.price !== null ? (line.product.price * line.qty).toFixed(2) : '',
      ]),
    [lines]
  );

  const handleCopy = useCallback(async () => {
    const text = [
      ['Item Code', 'Product', 'Shelf Label', 'Form', 'Qty', 'Unit Price', 'Line Total'].join('\t'),
      ...exportRows().map((row) => row.join('\t')),
    ].join('\n');
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Order draft copied');
    } catch {
      toast.error('Could not copy to clipboard');
    }
  }, [exportRows]);

  const handleDownload = useCallback(() => {
    const csv = [
      ['Item Code', 'Product', 'Shelf Label', 'Form', 'Qty', 'Unit Price', 'Line Total'],
      ...exportRows(),
    ]
      .map((row) => row.map(csvEscape).join(','))
      .join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `planogram-order-${new Date().toISOString().slice(0, 10)}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  }, [exportRows]);

  const handleClear = useCallback(() => {
    setQuantities({});
    toast.success('Draft cleared');
  }, []);

  if (authLoading) {
    return (
      <Screen>
        <Centered>
          <Spinner size={28} color={ThemeColors.primary.main} aria-label="Loading" />
        </Centered>
      </Screen>
    );
  }

  if (!isAuthorized) {
    return (
      <Screen>
        <Centered>
          <div>
            <Lock size={36} color={ThemeColors.gray[400]} aria-hidden="true" />
            <Title style={{ marginTop: Spacing.md }}>Not available</Title>
            <SourceNote style={{ maxWidth: 320 }}>
              The planogram order tool is still in internal testing and is limited to the account
              running the pilot. If you were expecting access, ask the portal administrator.
            </SourceNote>
            <PrimaryLink to="/" style={{ marginTop: Spacing.lg }}>
              Back to the portal
            </PrimaryLink>
          </div>
        </Centered>
      </Screen>
    );
  }

  if (loading) {
    return (
      <Screen>
        <Centered>
          <div>
            <Spinner size={28} color={ThemeColors.primary.main} aria-label="Loading" />
            <SourceNote>Loading the C120 planogram and portal products…</SourceNote>
          </div>
        </Centered>
      </Screen>
    );
  }

  if (error || !catalog || !display || !panel) {
    return (
      <Screen>
        <Centered>
          <div>
            <Title>Could not load the planogram</Title>
            <SourceNote style={{ maxWidth: 320 }}>{error ?? 'The layout data is empty.'}</SourceNote>
            <ReviewButton style={{ marginTop: Spacing.lg }} onClick={refetch}>
              <RefreshCw size={16} aria-hidden="true" /> Try again
            </ReviewButton>
          </div>
        </Centered>
      </Screen>
    );
  }

  const viewProps = {
    panel,
    quantities,
    onSelectSlot: setSelectedSlot,
    onChangeQuantity: setQuantity,
    query,
    filter,
  };

  return (
    <Screen>
      <Toaster position="top-center" />

      <Header>
        <TitleRow>
          <BackLink to="/" aria-label="Back to the portal">
            <ArrowLeft size={18} aria-hidden="true" />
          </BackLink>
          <TitleBlock>
            <Title>Planogram Ordering</Title>
            <Subtitle>{display.title}</Subtitle>
          </TitleBlock>
          <TestingBadge>Testing</TestingBadge>
          <IconButton
            $active={searchOpen || Boolean(query) || filter !== 'all'}
            onClick={() => setSearchOpen((open) => !open)}
            aria-label="Search and filter facings"
            aria-expanded={searchOpen}
          >
            <Search size={17} aria-hidden="true" />
          </IconButton>
          {display.pages.length > 0 && (
            <IconButton
              onClick={() => setLightboxPage(display.pages[0])}
              aria-label="Open the planogram reference page"
            >
              <ImageIcon size={17} aria-hidden="true" />
            </IconButton>
          )}
        </TitleRow>

        {/* Rack size and view mode share a row to keep the header short; the
            grid needs every pixel of vertical space it can get on a phone. */}
        <Toolbar>
          <Segmented role="tablist" aria-label="Rack size">
            {catalog.displays.map((option, index) => (
              <SegmentButton
                key={option.id}
                role="tab"
                aria-selected={index === displayIndex}
                $active={index === displayIndex}
                onClick={() => {
                  setDisplayIndex(index);
                  setPanelIndex(0);
                }}
              >
                {option.short}
              </SegmentButton>
            ))}
          </Segmented>
          <Segmented role="tablist" aria-label="View mode" style={{ flex: '0 0 auto' }}>
            {VIEW_OPTIONS.map((option) => (
              <SegmentButton
                key={option.id}
                role="tab"
                aria-selected={viewMode === option.id}
                title={option.hint}
                aria-label={`${option.label} view`}
                $active={viewMode === option.id}
                onClick={() => setViewMode(option.id)}
                style={{ padding: '0 9px', flex: '0 0 auto' }}
              >
                <option.icon size={15} aria-hidden="true" />
              </SegmentButton>
            ))}
          </Segmented>
        </Toolbar>

        <PillRow role="tablist" aria-label="Panel">
          {display.panels.map((option, index) => (
            <Pill
              key={option.label}
              role="tab"
              aria-selected={index === panelIndex}
              $active={index === panelIndex}
              onClick={() => setPanelIndex(index)}
            >
              Panel {option.letter}
            </Pill>
          ))}
        </PillRow>

        {searchOpen && (
          <>
            <Toolbar>
              <SearchBox>
                <Search size={15} color={ThemeColors.text.hint} aria-hidden="true" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Find a product, code or category"
                  aria-label="Search facings"
                  inputMode="search"
                  autoFocus
                />
                {query && (
                  <IconButton
                    style={{ width: 24, height: 24 }}
                    onClick={() => setQuery('')}
                    aria-label="Clear search"
                  >
                    <X size={13} aria-hidden="true" />
                  </IconButton>
                )}
              </SearchBox>
            </Toolbar>

            <PillRow aria-label="Filter facings">
              {FILTER_OPTIONS.map((option) => (
                <Pill
                  key={option}
                  $active={filter === option}
                  onClick={() => setFilter(option)}
                  aria-pressed={filter === option}
                >
                  {FILTER_LABELS[option]}
                </Pill>
              ))}
            </PillRow>
          </>
        )}

        {query && (
          <MatchRow>
            {panelMatches.total === 0 ? (
              <span>No facings match “{query}” on the {display.short} rack.</span>
            ) : (
              <>
                <span style={{ flexShrink: 0 }}>{panelMatches.total} on this rack:</span>
                {panelMatches.counts.map(
                  ({ letter, count }, index) =>
                    count > 0 && (
                      <MatchChip
                        key={letter}
                        $active={index === panelIndex}
                        onClick={() => setPanelIndex(index)}
                      >
                        Panel {letter} · {count}
                      </MatchChip>
                    )
                )}
              </>
            )}
          </MatchRow>
        )}
      </Header>

      <Body>
        {degraded && (
          <SourceNote style={{ color: ThemeColors.error.dark, marginTop: 0 }}>
            Portal products could not be loaded, so prices and stock status are unavailable. Shelf
            labels still reflect the planogram.
          </SourceNote>
        )}

        <PanelCaption>
          <div style={{ minWidth: 0 }}>
            <PanelName>{panel.label}</PanelName>
            <PanelShape>
              {panel.rowCount} shelves × {panel.colCount} facings
            </PanelShape>
          </div>
          {viewMode === 'rack' && (
            <Segmented style={{ flex: '0 0 auto' }} aria-label="Rack zoom">
              <ZoomIn
                size={13}
                aria-hidden="true"
                style={{ alignSelf: 'center', margin: '0 5px', color: ThemeColors.text.hint }}
              />
              {ZOOM_OPTIONS.map((option) => (
                <SegmentButton
                  key={option.id}
                  $active={zoom === option.id}
                  onClick={() => setZoom(option.id)}
                  style={{ padding: '0 9px', flex: '0 0 auto', minHeight: 30 }}
                  aria-label={`Zoom ${option.label}`}
                >
                  {option.label}
                </SegmentButton>
              ))}
            </Segmented>
          )}
        </PanelCaption>

        {viewMode === 'rack' && <RackView {...viewProps} zoom={zoom} />}
        {viewMode === 'shelf' && <ShelfView {...viewProps} />}
        {viewMode === 'list' && <ListView {...viewProps} />}

        {viewMode === 'rack' && (
          <Legend>
            {(Object.keys(FORM_COLORS) as (keyof typeof FORM_COLORS)[]).map((form) => (
              <LegendItem key={form}>
                <Swatch $color={FORM_COLORS[form]} />
                {FORM_LABELS[form]}
              </LegendItem>
            ))}
            <LegendItem>
              <Layers size={11} aria-hidden="true" />
              Row = shelf, column = facing
            </LegendItem>
          </Legend>
        )}

        <SourceNote>{catalog.note}</SourceNote>
      </Body>

      <OrderBar>
        <OrderSummary>
          <OrderPrimary>
            {lines.length === 0
              ? 'No products selected'
              : `${lines.length} product${lines.length === 1 ? '' : 's'} · ${totalUnits} units`}
          </OrderPrimary>
          <OrderSecondary>
            {lines.length === 0
              ? 'Tap any facing to set a quantity'
              : `Draft total $${totalValue.toFixed(2)} · saved on this device`}
          </OrderSecondary>
        </OrderSummary>
        <ReviewButton onClick={() => setReviewOpen(true)} disabled={lines.length === 0}>
          {lines.length === 0 ? (
            <ShoppingCart size={16} aria-hidden="true" />
          ) : (
            <ClipboardList size={16} aria-hidden="true" />
          )}
          Review
        </ReviewButton>
      </OrderBar>

      {selectedSlot && (
        <ProductSheet
          product={selectedSlot.product}
          slot={selectedSlot}
          facings={selectedFacings}
          panelLabel={panel.label}
          displayLabel={display.short}
          qty={quantities[selectedSlot.product.key] ?? 0}
          onChangeQuantity={(qty) => setQuantity(selectedSlot.product, qty)}
          onClose={() => setSelectedSlot(null)}
        />
      )}

      {reviewOpen && (
        <ReviewSheet
          lines={lines}
          onChangeQuantity={setQuantity}
          onClear={handleClear}
          onCopy={handleCopy}
          onDownload={handleDownload}
          onClose={() => setReviewOpen(false)}
        />
      )}

      {lightboxPage && (
        <Lightbox onClick={() => setLightboxPage(null)}>
          <LightboxClose onClick={() => setLightboxPage(null)} aria-label="Close reference page">
            <X size={20} aria-hidden="true" />
          </LightboxClose>
          <img src={`/assets/planograms/${lightboxPage}`} alt={`${display.title} planogram page`} />
        </Lightbox>
      )}
    </Screen>
  );
};
